import { inject, Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { InitialiseUserPreferences } from '@features/user-preference/store/actions/user-preference.actions';
import {
  DecodedIdToken,
  HermesError,
  HermesTokenResult,
  RefreshTokenExchangeResult,
  UserPreference,
} from '@kaligo/hermes';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  RdLibModuleCookiesConfig,
  RdLibModuleHermesModeConfig,
} from '@rd-lib/rd-lib.module.config';
import { SentryService } from '@rd-lib/sentry/sentry.service';
import { AppInit } from '@rd/app-init/app-init.action';
import * as mfaActions from '@rd/core/mfa/store/actions/mfa.actions';
import {
  MFAAutoRedemption,
  MFAOneTimeRedemption,
  MFAOutcome,
  MFAShoppingCart,
} from '@rd/core/mfa/types';
import { UserAgentService } from '@rd/core/services/user-agent/user-agent.service';
import { CORE_AUTH_SESSION_EXPIRED_DIALOG } from '@rd/core/tokens/popup.token';
import {
  COOKIES_CONFIG,
  HERMES_MODE,
  TENANT_NAME,
} from '@rd/core/tokens/rd-lib.token';
import { GuardhouseErrorCodes } from '@rd/errors/guardhouse-error-codes.type';
import { HermesAuthError } from '@rd/errors/hermes-auth-error';
import { HermesErrorCodes } from '@rd/errors/hermes-error-codes.type';
import { NydusNetworkAuthError } from '@rd/errors/nydus-network-auth-error';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { HttpErrorDetails, TransactionAppState, User } from '@rd/types';
import { set as setCookie } from 'es-cookie';
import { concat, defer, EMPTY, from, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  map,
  mapTo,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AuthErrorMessageDialogComponent } from '../../../components/auth-error-message-dialog.component/auth-error-message-dialog.component';
import * as currencyActions from '../../../currency/store/actions/currency.actions';
import { GuardhouseService } from '../../../services/guardhouse/guardhouse.service';
import { HermesService } from '../../../services/hermes/hermes.service';
import * as actions from '../actions/auth.actions';
import { AuthActionTypes } from '../actions/auth.actions';
import {
  getLoginRefParamValue,
  sanitizeLoginRef,
} from '../helpers/login-ref.helper';
import { AuthState } from '../reducers/auth.reducer';
import {
  getAuthIsIdleCheckRunning,
  getAuthIsLoggedIn,
  getAuthIsSessionCheckRunning,
  getAuthToken,
  getAuthUser,
} from '../selectors/auth.selectors';

const LOGIN_REF_STORAGE_KEY = 'redirect_ref';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private hermesService: HermesService,
    private sentryService: SentryService,
    private guardhouseService: GuardhouseService,
    private store: Store<AuthState>,
    private router: Router,
    private dialogService: DialogService,
    private translateService: TranslateService,
    private userAgentService: UserAgentService,
    @Inject(TENANT_NAME) private tenantName: string,
    @Inject(COOKIES_CONFIG)
    private cookiesConfig: RdLibModuleCookiesConfig,
    @Inject(HERMES_MODE) private hermesMode: RdLibModuleHermesModeConfig,
  ) {}

  private coreAuthSessionExpiredDialogComponentType = inject(
    CORE_AUTH_SESSION_EXPIRED_DIALOG,
  );

  appInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppInit),
      map(() => {
        switch (this.hermesMode) {
          case 'iframe':
            return new actions.StartIframeTokenRefresh();
          case 'refresh-token-rotation':
            return new actions.InitLoginRedirect();
        }
      }),
    ),
  );

  initLoginRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.INIT_LOGIN_REDIRECT),
      switchMap(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('redirect_required') === 'true') {
          return concat(
            from(this.getLoginRefActions()),
            defer(() => {
              this.guardhouseService.redirectToLoginUrl();
              return EMPTY;
            }),
          );
        } else {
          return of(new actions.HandleLoginRedirect());
        }
      }),
    ),
  );

  // Runs once when the effect is registered (before AppInit), so a `ref`
  // captured from the URL in initLoginRedirect$ always wins. ROOT_EFFECTS_INIT
  // is not usable here: it fires before feature effects subscribe.
  rehydrateLoginRef$ = createEffect(() =>
    defer(() => {
      const ref = sanitizeLoginRef(
        this.readSessionStorage(LOGIN_REF_STORAGE_KEY),
      );
      return ref ? of(new actions.SetLoginRef(ref)) : EMPTY;
    }),
  );

  persistLoginRef$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.SET_LOGIN_REF),
        tap(({ payload }: actions.SetLoginRef) => {
          const ref = sanitizeLoginRef(payload);

          if (ref) {
            this.writeSessionStorage(LOGIN_REF_STORAGE_KEY, ref);
          }
        }),
      ),
    { dispatch: false },
  );

  clearLoginRef$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AuthActionTypes.LOGOUT_USER_COMPLETE,
          AuthActionTypes.SESSION_EXPIRED_FAIL,
        ),
        tap(() => this.removeSessionStorage(LOGIN_REF_STORAGE_KEY)),
      ),
    { dispatch: false },
  );

  handleLoginRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.AuthActionTypes.HANDLE_LOGIN_REDIRECT),
      exhaustMap(() => {
        const refActions = this.getLoginRefActions();

        return from(this.hermesService.handleRedirect()).pipe(
          // The ref is saved once hermes handle redirect with success,
          // other cases should ignore the `ref` param saving
          switchMap((transactionAppState) =>
            from([
              ...(transactionAppState ? refActions : []),
              new actions.StartRTRTokenRefresh(transactionAppState),
            ]),
          ),
          catchError((error) => of(new actions.LoadTokenFail(error))),
        );
      }),
    ),
  );

  startIframeTokenRefresh$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.AuthActionTypes.START_IFRAME_TOKEN_REFRESH),
        tap(() => this.hermesService.startIframeTokenRefresh()),
      ),
    { dispatch: false },
  );

  startRTRTokenRefresh$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.AuthActionTypes.START_RTR_TOKEN_REFRESH),
        tap(({ payload }) => this.hermesService.startRTRTokenRefresh(payload)),
      ),
    { dispatch: false },
  );

  initialiseUserPreference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.AuthActionTypes.LOAD_RTR_TOKEN_SUCCESS,
        actions.AuthActionTypes.LOAD_IFRAME_TOKEN_SUCCESS,
      ),
      take(1),
      map(
        ({ payload: { tokenResult } }) =>
          new InitialiseUserPreferences(
            this.hermesService.parsePreferences(tokenResult),
          ),
      ),
    ),
  );

  loadIframeTokenSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.AuthActionTypes.LOAD_IFRAME_TOKEN_SUCCESS),
      withLatestFrom(
        this.store.select(getAuthIsIdleCheckRunning),
        this.store.select(getAuthIsSessionCheckRunning),
      ),
      exhaustMap(
        ([{ payload }, idleCheckRunning, sessionCheckRunning]: [
          { payload: { tokenResult: HermesTokenResult } },
          boolean,
          boolean,
        ]) =>
          from(this.hermesService.handleRedirect()).pipe(
            exhaustMap((transactionAppState) => {
              const user = this.hermesService.parseToken(
                new DecodedIdToken(payload.tokenResult.idToken),
              );
              const userPreference = this.hermesService.parsePreferences(
                payload.tokenResult,
              );
              return this.getLoginActions(
                user,
                userPreference,
                idleCheckRunning,
                sessionCheckRunning,
                transactionAppState,
              );
            }),
          ),
      ),
    ),
  );

  loadRTRTokenSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.AuthActionTypes.LOAD_RTR_TOKEN_SUCCESS),
      withLatestFrom(
        this.store.select(getAuthIsIdleCheckRunning),
        this.store.select(getAuthIsSessionCheckRunning),
      ),
      exhaustMap(
        ([
          {
            payload: { tokenResult, transactionAppState },
          },
          idleCheckRunning,
          sessionCheckRunning,
        ]: [
          {
            payload: {
              tokenResult: RefreshTokenExchangeResult;
              transactionAppState: TransactionAppState;
            };
          },
          boolean,
          boolean,
        ]) => {
          const user = this.hermesService.parseToken(
            tokenResult.decodedIdToken,
          );
          const userPreference =
            this.hermesService.parsePreferences(tokenResult);
          return this.getLoginActions(
            user,
            userPreference,
            idleCheckRunning,
            sessionCheckRunning,
            transactionAppState,
          );
        },
      ),
    ),
  );

  loadTokenFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.AuthActionTypes.LOAD_TOKEN_FAIL),
      withLatestFrom(this.store.select(getAuthIsLoggedIn)),
      map(
        ([{ payload: error }, isLoggedIn]: [
          actions.LoadTokenFail,
          boolean | null,
        ]) => {
          switch (error.code) {
            case HermesErrorCodes.MISSING_REFRESH_TOKEN:
              return new actions.LoadTokenOtherFail(error);
            case HermesErrorCodes.INVALID_TOKEN:
              return isLoggedIn
                ? new actions.LoadTokenOtherFail(error)
                : new actions.LogoutUser();
            case GuardhouseErrorCodes.SESSION_EXPIRED:
              return isLoggedIn
                ? new actions.SessionExpiredFail(error)
                : new actions.LogoutUser();
            case GuardhouseErrorCodes.REDIRECT_REQUIRED:
              return new actions.LoadTokenRedirectRequiredFail();
            default:
              return new actions.LoadTokenOtherFail(error);
          }
        },
      ),
    ),
  );

  redirectToLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.AuthActionTypes.REDIRECT_TO_LOGIN),
        tap(() => this.guardhouseService.redirectToLoginUrl()),
      ),
    { dispatch: false },
  );

  startSessionCheck$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.AuthActionTypes.START_SESSION_CHECK),
        tap(() => this.hermesService.startSessionCheck()),
      ),
    { dispatch: false },
  );

  sessionCheckFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.AuthActionTypes.SESSION_CHECK_FAIL),
      map(({ payload: error }: actions.SessionCheckFail) => {
        switch (error.code) {
          case GuardhouseErrorCodes.SESSION_EXPIRED:
            return new actions.SessionExpiredFail(error);
          default:
            return new actions.SessionCheckOtherFail(error);
        }
      }),
    ),
  );

  startIdleCheck$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.AuthActionTypes.START_IDLE_CHECK),
        tap(() => this.hermesService.startIdleCheck()),
      ),
    { dispatch: false },
  );

  logoutUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.AuthActionTypes.LOGOUT_USER),
      withLatestFrom(this.store.select(getAuthToken)),
      tap(
        ([action, token]: [
          actions.LogoutUser,
          HermesTokenResult | RefreshTokenExchangeResult | null,
        ]) => {
          this.hermesService.stopSessionCheck();
          this.hermesService.stopTokenRefresh();

          if (token) {
            this.guardhouseService.redirectToLogoutUrl(action.payload);
          }
        },
      ),
      mapTo(new actions.LogoutUserComplete()),
    ),
  );

  fatalHermesFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.AuthActionTypes.LOAD_TOKEN_OTHER_FAIL,
        actions.AuthActionTypes.SESSION_CHECK_OTHER_FAIL,
      ),
      withLatestFrom(
        this.store.select(getAuthUser),
        this.store.select(getAuthToken),
      ),
      exhaustMap(
        ([action, user, tokenResult]: [
          actions.LoadTokenOtherFail | actions.SessionCheckOtherFail,
          User | null,
          HermesTokenResult | RefreshTokenExchangeResult | null,
        ]) => {
          this.reportHermesError(action, action.payload, user, tokenResult);
          return of(new actions.LogoutUser());
        },
      ),
    ),
  );

  fatalNydusNetworkFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.AuthActionTypes.INTERNAL_FAIL,
        actions.AuthActionTypes.MAXIMUM_RETRIES_FAIL,
        actions.AuthActionTypes.TOKEN_EXPIRED_FAIL,
      ),
      exhaustMap(
        (
          action:
            | actions.InternalFail
            | actions.MaximumRetriesFail
            | actions.TokenExpiredFail,
        ) => {
          if (action.payload.errors) {
            this.reportNydusNetworkError(action, action.payload.errors);
          }
          return of(new actions.LogoutUser());
        },
      ),
    ),
  );

  invalidScopeFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.AuthActionTypes.INVALID_SCOPE_FAIL),
        // user is not logged in immediately, as this error only signifies
        // that a certain action in NN failed due to invalid scope / lack of scopes
        tap((action: actions.InvalidScopeFail) => {
          if (action.payload.errors) {
            this.reportNydusNetworkError(action, action.payload.errors);
          }
        }),
      ),
    { dispatch: false },
  );

  redirectRequiredFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.AuthActionTypes.LOAD_TOKEN_REDIRECT_REQUIRED_FAIL),
        // Occurs when user logs in to TE first, but hasn't gone to RD yet.
        // In this case user needs to log in separately to RD as well.
        tap(() => this.guardhouseService.redirectToLoginUrl()),
      ),
    { dispatch: false },
  );

  noPermissionFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.AuthActionTypes.NO_PERMISSION_FAIL),
        tap(() => {
          this.dialogService.open(AuthErrorMessageDialogComponent, {
            id: 'auth-error-message-dialog',
            screenMode: 'center-md',
            ariaLabel: this.translateService.instant(
              'auth.error_message_no_permission',
            ),
          });
        }),
      ),
    { dispatch: false },
  );

  sessionExpiredFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.AuthActionTypes.SESSION_EXPIRED_FAIL),
        tap((action: actions.SessionExpiredFail) => {
          this.dialogService.open<{ errors: HermesError[] }>(
            this.coreAuthSessionExpiredDialogComponentType,
            {
              data: {
                errors: [action.payload],
              },
              id: 'session-expired-dialog',
              screenMode: 'center-md',
              ariaLabel: this.translateService.instant(
                'auth.session_expired_dialog.error_message',
              ),
            },
          );
        }),
      ),
    { dispatch: false },
  );

  loadTokenUserPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.AuthActionTypes.LOAD_TOKEN_USER_PREFERENCES),
      exhaustMap((action: actions.LoadTokenUserPreferences) => {
        const preferences = action.payload;
        const preferenceUpdates = [];

        if (preferences.currency) {
          preferenceUpdates.push(
            new currencyActions.ChangeCurrencyByCode(preferences.currency),
          );
        }

        // TODO: Add loyalty currency switch. No loyalty currency impl atm
        return preferenceUpdates;
      }),
    ),
  );

  uploadLocaleCookieBrowser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.AuthActionTypes.UPDATE_LOCALE_COOKIE),
        tap((action: actions.UpdateLocaleCookie) => {
          setCookie(this.cookiesConfig.localeCookieName, action.payload, {
            domain: this.cookiesConfig.localeCookieDomain,
            sameSite: 'lax',
          });
        }),
      ),
    { dispatch: false },
  );

  private getLoginActions(
    user: User,
    userPreference: UserPreference,
    idleCheckRunning: boolean,
    sessionCheckRunning: boolean,
    transactionAppState: TransactionAppState | null,
  ): (
    | actions.AuthAction
    | ReturnType<typeof mfaActions.RestoreStateFromMFA>
  )[] {
    const loginActions: (
      | actions.AuthAction
      | ReturnType<typeof mfaActions.RestoreStateFromMFA>
    )[] = [
      new actions.LoginUser(user),
      new actions.LoadTokenUserPreferences(userPreference),
    ];

    if (!idleCheckRunning) {
      loginActions.push(new actions.StartIdleCheck());
    }

    if (!sessionCheckRunning) {
      loginActions.push(new actions.StartSessionCheck());
    }

    if (transactionAppState?.currentUrl) {
      this.router.navigateByUrl(transactionAppState?.currentUrl);
    } else if (transactionAppState) {
      const hydrationState = transactionAppState as
        | MFAAutoRedemption
        | MFAOneTimeRedemption
        | MFAShoppingCart;
      const outcome = window.location.toString().includes('outcome=success')
        ? MFAOutcome.Success
        : MFAOutcome.Failure;
      loginActions.push(
        mfaActions.RestoreStateFromMFA({ data: hydrationState, outcome }),
      );
    }

    // defensive guard to make sure undefined user won't break this effect
    if (user?.sub) {
      this.sentryService.trackUser({ userId: user.sub });
    }

    return loginActions;
  }

  private getLoginRefActions(): actions.SetLoginRef[] {
    const ref = getLoginRefParamValue(
      new URLSearchParams(window.location.search),
    );

    return ref ? [new actions.SetLoginRef(ref)] : [];
  }

  // sessionStorage can throw (privacy mode, blocked storage); the ref then
  // just lives in memory for this page load.
  private readSessionStorage(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writeSessionStorage(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Storage unavailable: the ref stays in memory for this page load.
    }
  }

  private removeSessionStorage(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Storage unavailable: nothing was persisted.
    }
  }

  private reportHermesError(
    action: actions.AuthAction,
    error: HermesError,
    user: User | null,
    tokenResult: HermesTokenResult | RefreshTokenExchangeResult | null,
  ): void {
    this.sentryService.manualHermesAuthError(
      new HermesAuthError(error),
      action,
      user?.sub,
      (tokenResult as RefreshTokenExchangeResult)?.refreshToken,
    );
  }

  private reportNydusNetworkError(
    action: actions.AuthAction,
    errors: HttpErrorDetails[],
  ): void {
    this.sentryService.manualNydusNetworkAuthError(
      new NydusNetworkAuthError(errors),
      action,
    );
  }
}
